import axios from 'axios';
import { useAuthStore } from '../store/authStore';

export function resolveApiBaseUrl(configuredUrl: string | undefined, production: boolean): string {
  if (!configuredUrl) return '/api';
  if (!production) return configuredUrl;
  try {
    const hostname = new URL(configuredUrl).hostname.toLowerCase();
    if (hostname === 'localhost' || hostname === '::1' || hostname.startsWith('127.')) return '/api';
  } catch {
    // Relative API paths are valid and remain same-origin.
  }
  return configuredUrl;
}

export const API_BASE_URL = resolveApiBaseUrl(import.meta.env.VITE_API_URL, import.meta.env.PROD);

const client = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

client.interceptors.request.use((config) => {
  const token = localStorage.getItem('awarenow_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// A forced password change already lands on Settings. Reloading that same
// URL makes the page refresh forever when another request is still blocked.
export function passwordChangeLocation(pathname: string): string | null {
  const path = pathname.replace(/\/$/, '') || '/';
  if (path === '/settings') return null;
  return '/settings';
}

client.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    const message = String(error.response?.data?.message || '');
    if (status === 403 && /password change required/i.test(message)) {
      const next = passwordChangeLocation(window.location.pathname);
      if (next) window.location.href = next;
      return Promise.reject(error);
    }
    const path = error.config?.url?.split('?')[0]?.replace(/\/$/, '');
    const credentialValidation = path === '/login' || path === '/reset_password' || path === '/session' || path === '/logout';
    if (status === 401 && !credentialValidation) {
      useAuthStore.getState().logout();
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export default client;
